import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { hospitalAdminApi, type CampaignAttributionRow } from "@/api/hospitalAdmin";

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function pct(n: number) {
  return `${n.toFixed(1)}%`;
}

export default function CampaignAttributionDashboardPage() {
  const initial = useMemo(() => defaultRange(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [campaignId, setCampaignId] = useState("");
  const [applied, setApplied] = useState({ from: initial.from, to: initial.to, campaignId: "" });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["campaign-attribution", applied],
    queryFn: () =>
      hospitalAdminApi.getCampaignAttribution({
        from: applied.from,
        to: applied.to,
        ...(applied.campaignId.trim() ? { campaignId: applied.campaignId.trim() } : {}),
      }),
  });

  const rows = data?.rows ?? [];
  const totals = data?.totals;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Meta Attribution</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Campaign-level funnel from lead capture through registrations, appointments, admissions, surgeries, and revenue.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div>
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="min-w-[200px]">
            <Label htmlFor="campaignId">Campaign ID</Label>
            <Input
              id="campaignId"
              placeholder="Optional Meta campaign id"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
            />
          </div>
          <Button
            onClick={() => setApplied({ from, to, campaignId })}
            disabled={isFetching}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => void refetch()} disabled={isFetching}>
            Refresh
          </Button>
        </CardContent>
      </Card>

      {totals ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            ["Leads", totals.leads],
            ["Registrations", totals.registrations],
            ["Appointments", totals.appointments],
            ["Admissions", totals.admissions],
            ["Surgeries", totals.surgeries],
            ["Revenue", `₹${totals.revenue.toLocaleString()}`],
            ["Lead → Appt", pct(totals.conversionRates.leadToAppointment)],
            ["Appt → Surgery", pct(totals.conversionRates.appointmentToSurgery)],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-xl font-semibold">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>By campaign</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No attributed leads in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3">Campaign</th>
                    <th className="py-2 pr-3 text-right">Leads</th>
                    <th className="py-2 pr-3 text-right">Regs</th>
                    <th className="py-2 pr-3 text-right">Appts</th>
                    <th className="py-2 pr-3 text-right">Admits</th>
                    <th className="py-2 pr-3 text-right">Surgeries</th>
                    <th className="py-2 pr-3 text-right">Revenue</th>
                    <th className="py-2 pr-3 text-right">L→A</th>
                    <th className="py-2 text-right">A→S</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: CampaignAttributionRow) => (
                    <tr key={row.campaignId} className="border-b">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{row.campaignName || row.campaignId}</div>
                        {row.campaignName ? (
                          <div className="text-xs text-muted-foreground">{row.campaignId}</div>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3 text-right">{row.leads}</td>
                      <td className="py-2 pr-3 text-right">{row.registrations}</td>
                      <td className="py-2 pr-3 text-right">{row.appointments}</td>
                      <td className="py-2 pr-3 text-right">{row.admissions}</td>
                      <td className="py-2 pr-3 text-right">{row.surgeries}</td>
                      <td className="py-2 pr-3 text-right">₹{row.revenue.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right">{pct(row.conversionRates.leadToAppointment)}</td>
                      <td className="py-2 text-right">{pct(row.conversionRates.appointmentToSurgery)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
